import { FaCheck, FaEdit, FaRegHandSpock, FaShieldAlt } from "react-icons/fa";
import moment from "moment";
import { FaClockRotateLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppTableFields } from "../../../constants/AppointmentConstants";
import CustomTable from "../../../shared/CustomTable";
import { CancelIcon, EditIcon } from "../../../utils/TableActionIcons";
import CustomPagination from "../../../shared/CustomPagination";
import ConfirmationModal from "../../../shared/ConfirmationModal";
import { MdOutlineTrackChanges, MdVerified } from "react-icons/md";
import toast from "react-hot-toast";
import {
  useCancelAppointmentMutation,
  useConfirmAppointmentMutation,
} from "../../../redux/api/modules/appointmentsApi";
import CheckPermission from "../../../hooks/CheckPermission";
import { fetchAppointmentLIst } from "./ViewAllAppointments";
import ReusableModal from "../../../shared/ReuseableModal";
import CancelAppointmentForm from "./CancelAppointmentForm";
import { handleCheckPatientElibility } from "../../../services/ApiMethods";
import { EligibilityDataComponent } from "../../../pages/HIS/CreateVisit";
import { useState } from "react";
import Spinner from "../../../shared/Spinner";
import { Skeleton } from "primereact/skeleton";

const SkeletonEligibilityLoading = ({ t }) => (
  <div className="flex flex-col gap-6 p-6">
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Skeleton width="100%" height="2.5rem" borderRadius="9999px" />
        <Skeleton width="100%" height="2.5rem" borderRadius="9999px" />
      </div>
    </div>
    <div className="flex items-center gap-3">
      <Skeleton width="100%" height="1.8rem" borderRadius="9999px" />
      <Skeleton width="100%" height="1.8rem" borderRadius="9999px" />
    </div>
  </div>
);

const AppointmentsTable = ({
  appointmentData,
  loading,
  payloadData,
  setPayloadData,
  refetch,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedAppointmentData, setSelectedAppointmentData] = useState(null);
  const [openCancelAppointmentModal, setOpenCancelAppointmentModal] =
    useState(false);
  const [actionType, setActionType] = useState("");
  const [cancelAppointment, { isLoading: cancelLoading }] = useCancelAppointmentMutation();
  const [confirmAppointment, { isLoading: confirmLoading }] = useConfirmAppointmentMutation();
  const buttonLoading = cancelLoading || confirmLoading;
  const { hasPermission } = CheckPermission();
  const [checkLoading, setCheckLoading] = useState(false);
  const [eligibilityPayload, setEligibilityPayload] = useState({
    payer_id: '',
    rcm_ref: '',
    insurance_plan_id: ''
  });
  const [openEligibilityModel, setOpenEligibilityModel] = useState(false);
  const [currentInsurancePlans, setCurrentInsurancePlans] = useState([]);
  const [eligibilityData, setEligibilityData] = useState(null);

  const actions = {
    header: t("Actions"),
    body: ActionTempelate,
  };

  const handleCheckEligibilityCall = async (plan, row) => {
    if (!plan || !row) return;
    setCheckLoading(true);
    try {
      const payload = {
        payer_id: plan?.payer_id,
        rcm_ref: row?.patient?.rcm_ref,
        insurance_plan_id: plan?.insurance_plan_id,
      };
      setEligibilityPayload(payload);
      let eligibilityResponse = await handleCheckPatientElibility(payload);
      if (eligibilityResponse) {
        setEligibilityData(eligibilityResponse);
      }
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to check eligibility"));
    } finally {
      setCheckLoading(false);
    }
  };

  const handleCheckEligibilityInit = (row) => {
    setSelectedAppointmentData(row);
    setEligibilityData(null);
    setCurrentInsurancePlans(row?.patient?.insurance_plans || []);
    setOpenEligibilityModel(true);

    if (row?.patient?.insurance_plans?.length === 1) {
      handleCheckEligibilityCall(row?.patient?.insurance_plans[0], row);
    }
  };

  function ActionTempelate(row) {
    return (
      <div className="flex gap-1">
        {row?.status !== "Cancelled" && row?.status !== "completed" && (
          <>

            {hasPermission("appointments", "update") && (
              <CancelIcon
                title={t("Cancel")}
                iconSize={18}
                onClick={() => {
                  setOpenCancelAppointmentModal(true);
                  setSelectedAppointmentData(row);
                  setActionType("cancel");
                }}
              />
            )}
            {hasPermission("appointments", "update") && row?.status !== "Fulfilled" && (
              <EditIcon
                iconSize={18}
                title={t("Edit")}
                className={`mx-0 ${i18n.language === 'ar' ? 'mr-2' : 'ml-2'}`}
                onClick={() =>
                  navigate(`/edit-appointment/${row?.appointment_id}`)
                }
              />
            )}
            {hasPermission("appointments", "create") && (
              <FaClockRotateLeft
                size={18}
                className="text-gray-500 cursor-pointer ms-2"
                title={t("Reschedule")}
                onClick={() => {
                  navigate("/create-appointment", {
                    state: { appointmentData: row },
                  });
                }}
              />
            )}
          </>
        )}
        <MdOutlineTrackChanges
          size={18}
          className="text-primary cursor-pointer ms-2"
          title={t("Track")}
          onClick={async () => {
            navigate(`/appointment-history/${row?.appointment_id}`, {
              state: { rowData: row },
            });
          }}
        />
        {row?.status !== "Cancelled" && row?.patient?.beneficiary_type == "insurance" && (
          <MdVerified
            size={20}
            className="text-green-500 cursor-pointer ms-2"
            title={t("Check Eligibility")}
            onClick={() => handleCheckEligibilityInit(row)}
          />
        )}
      </div>
    );
  }

  const columns = [actions, ...AppTableFields(t, payloadData)];

  const onPageChange = async (newPage, newRowsPerPage) => {
    let payload = {
      ...payloadData,
      page: newPage,
      per_page: newRowsPerPage,
    };
    setPayloadData(payload);
  };

  const handleCancelAppointment = async (reason) => {
    const payload = {
      appointment_id: selectedAppointmentData?.appointment_id,
      practitioner_id: selectedAppointmentData?.practitioner?.practitioner_id,
      reason: reason,
    };
    try {
      const res = await cancelAppointment(payload).unwrap();
      toast.success(res?.message || t("Appointment cancelled successfully"));
      setOpenCancelAppointmentModal(false);
      setSelectedAppointmentData(null);
    } catch (error) {
      toast.error(error?.data?.message || error?.message || t("Something went wrong"));
    }
  };

  const handleConfirmAppoint = async () => {
    const payload = { appointment_id: selectedAppointmentData?.appointment_id };
    try {
      const res = await confirmAppointment(payload).unwrap();
      toast.success(res?.message || t("Appointment confirmed successfully"));
      setOpenCancelAppointmentModal(false);
      setSelectedAppointmentData(null);
    } catch (error) {
      toast.error(error?.data?.message || error?.message || t("Something went wrong"));
    }
  };

  return (
    <div className="w-full h-full">
      <CustomTable
        data={appointmentData?.data || []}
        columns={columns}
        isLoading={loading}
        emptyMessage={t("No Appointment Data Found")}
      />
      {appointmentData?.data?.length > 0 && (
        <CustomPagination
          currentPage={appointmentData?.page || payloadData?.page || 1}
          rowsPerPage={appointmentData?.per_page || payloadData?.per_page || 20}
          totalRecords={appointmentData?.total_records || 0}
          totalPages={appointmentData?.total_pages || 1}
          onPageChange={onPageChange}
        />
      )}
      <ReusableModal
        className="w-full md:w-[90%] lg:w-[800px]"
        position="top"
        title={t("Cancel Appointment")}
        isVisible={openCancelAppointmentModal}
        HeaderIcon={CancelIcon}
        onClose={() => {
          setSelectedAppointmentData(null);
          setOpenCancelAppointmentModal(false);
        }}
      >
        <CancelAppointmentForm
          handleCancel={handleCancelAppointment}
          cancelLoading={buttonLoading}
        />
      </ReusableModal>
      <ReusableModal
        isVisible={openEligibilityModel}
        position="top"
        title={t("Eligibility Verification")}
        className="w-full md:w-[800px] lg:w-[1000px]"
        HeaderIcon={FaRegHandSpock}
        onClose={() => {
          setOpenEligibilityModel(false);
          setEligibilityData(null);
          setCurrentInsurancePlans([]);
        }}
      >
        {checkLoading ? (
          <SkeletonEligibilityLoading t={t} />
        ) : eligibilityData ? (
          <EligibilityDataComponent eligibilityData={eligibilityData} />
        ) : currentInsurancePlans?.length > 1 ? (
          <div className="flex flex-col gap-5 p-2">
            <div className="flex items-center gap-3 border-b border-gray-100 pb-4">

              <div>
                <h3 className="text-lg font-bold text-gray-800">{t("Select Insurance Plan")}</h3>
                <p className="text-sm text-gray-500">{t("Multiple plans found for this patient. Please choose one to verify.")}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentInsurancePlans.map((plan, index) => (
                <div
                  key={index}
                  className="group relative flex flex-col p-5 border border-gray-100 rounded-2xl hover:border-primary hover:bg-indigo-50/30 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-lg bg-white overflow-hidden"
                  onClick={() => handleCheckEligibilityCall(plan, selectedAppointmentData)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1.5">
                        {plan?.is_primary ? t("Primary Plan") : t("Secondary Plan")}
                      </span>
                      <h3 className="text-base font-bold text-gray-800 group-hover:text-primary transition-colors line-clamp-1">
                        {plan?.payer_name || plan?.name || t("Unknown Payer")}
                      </h3>
                    </div>
                    {plan?.eligiblity_status && (
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${plan?.eligiblity_status?.toLowerCase() === 'active' || plan?.eligiblity_status?.toLowerCase() === 'eligible'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-red-100 text-red-600'
                        }`}>
                        {plan?.eligiblity_status}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-auto pt-4 border-t border-gray-50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{t("Member ID")}</span>
                      <span className="text-sm font-semibold text-gray-700">{plan?.member_card_id || '—'}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{t("Class")}</span>
                      <span className="text-sm font-semibold text-gray-700">{plan?.class || 'Standard'}</span>
                    </div>
                  </div>

                  <div className="absolute -right-2 -bottom-2 w-16 h-16 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors"></div>

                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-16 text-gray-400 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-100">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm mb-4">
              <FaShieldAlt size={32} className="opacity-20" />
            </div>
            <p className="font-medium">{t("No insurance details available for verification")}</p>
          </div>
        )}
      </ReusableModal>
    </div>
  );
};

export default AppointmentsTable;
